import { BaseElement } from 'base';
import { cart } from 'cart';
import { giftOffer, GIFT_ATTRIBUTE, GIFT_PWT } from 'gift-offer';
import { sectionFragment } from 'section-fragment';
import { withTimeout } from 'with-timeout';

const SELECTION_TOPIC = 'product:selection';
const MEMBER_ID = 'GiftItem';
const LIST = '[data-gift-list]';
const COLOR = '[data-gift-color]';
const SIZE = '[data-gift-size]';
const COLOR_FIELD = '[data-gift-field="color"]';
const SIZE_FIELD = '[data-gift-field="size"]';
const SELECT = `${COLOR}, ${SIZE}`;
const READY_WAIT = 1800;
const CART_WAIT = 800;
const READY_CAP = 10000;
const MAX_SLOTS = 3;
const SWAP_REGIONS = ['[data-gift-thumb]', '[data-gift-head]'];
const SETTLED_MARK = 'gift-block:settled';
const LOGGED_REASONS = [
  'api-timeout',
  'api-failed',
  'cards-failed',
  'cap',
  'build-error'
];
const BUILT_REASONS = [
  'no-offer',
  'cards-failed',
  'inline-rendered',
  'fallback-rendered'
];

const normalize = value =>
  String(value ?? '')
    .trim()
    .toLowerCase();

function readMember(handle, html, allowed) {
  const root = sectionFragment({ [handle]: html }, handle, MEMBER_ID);
  if (!root) return null;
  const options =
    root.querySelector('template[data-gift-sizes]')?.content.children ?? [];
  const sizes = [...options]
    .map(option => ({
      id: Number(option.value),
      label: option.textContent.trim(),
      available: !option.disabled
    }))
    .filter(size => !allowed || allowed.has(size.id));
  const defaultId = Number(root.dataset.defaultVariant);
  const fallback = sizes.find(size => size.available)?.id ?? null;
  return {
    root,
    color: root.dataset.color ?? '',
    sizes,
    defaultVariant: sizes.some(size => size.id === defaultId && size.available)
      ? defaultId
      : fallback,
    available: root.dataset.available === 'true' && fallback !== null
  };
}

const hasVariant = (member, id) =>
  member.sizes.some(size => size.id === id && size.available);

const holdsVariant = (member, id) =>
  member.root ? hasVariant(member, id) : (member.variantIds ?? []).includes(id);

const cloneTemplate = (root, selector) => {
  const template = root.querySelector(selector);
  return template ? document.importNode(template.content, true) : null;
};

class GiftBlock extends BaseElement {
  #generation = 0;
  #ready = Promise.resolve(null);
  #resolveReady = () => {};
  #settled = false;
  #built = false;
  #offer = null;
  #found = null;
  #loaded = false;
  #capTimer = null;
  #selection = null;
  #slots = [];
  #startedAt = 0;

  onConnect() {
    const built = this.#built;
    if (!built) {
      this.#selection = null;
      this.#loaded = false;
      this.#found = null;
    } else {
      for (const slot of this.#slots) this.#revertPending(slot);
    }
    this.cleanup(() => {
      this.#generation += 1;
      clearTimeout(this.#capTimer);
    });
    this.listen(this, 'change', this.#onChange);
    this.listen(this, 'pointerdown', this.#onPointerDown);
    this.listen(this, 'keydown', this.#clearPointer);
    this.listen(this, 'pointercancel', this.#clearPointer);
    this.listen(this, 'focusout', this.#clearPointer);
    if (this.dataset.sectionId)
      this.subscribe(SELECTION_TOPIC, this.#onSelection);
    if (!built) this.#build();
  }

  setSelection(selection) {
    if (selection) this.#select(selection);
  }

  async cartItems() {
    const deadline = Date.now() + READY_WAIT;
    const offer = await this.#readyOffer();
    const found = this.#found;
    if (!found) return [];

    const remaining = () => Math.max(deadline - Date.now(), 0);
    const [attribute] = await Promise.all([
      withTimeout(
        giftOffer.attribute(this.dataset.shop, found.ruleId),
        remaining(),
        'gift attribute was not ready in time'
      ).catch(() => null),
      withTimeout(
        Promise.all(
          this.#slots.filter(slot => slot.pending).map(slot => slot.settling)
        ),
        remaining(),
        'gift colour switch was not ready in time'
      ).catch(() => null)
    ]);
    for (const slot of this.#slots) this.#revertPending(slot);
    if (!attribute) {
      console.error(
        '[gift-block] gifts skipped, attribute not ready',
        `${this.#elapsed()}ms source=${found.source}`
      );
      return [];
    }
    if (this.#found !== found) return [];

    const data = await withTimeout(
      cart.get(),
      CART_WAIT,
      'cart read timed out'
    ).catch(error => {
      console.error('[gift-block] cart read failed, gifts skipped:', error);
      return null;
    });
    const key = data
      ? giftOffer.bundleKey(found.ruleId, data.items)
      : giftOffer.unlinkedKey(found.ruleId);
    if (!key) return [];

    const properties = { _bundle: `${key}#`, _pwt: GIFT_PWT };
    for (const slot of this.#slots) this.#revertPending(slot);
    const items =
      data && offer?.ruleId === found.ruleId ? this.#giftItems(key) : [];
    if (!items.length) return { items, properties };

    return {
      items,
      properties,
      attributes: {
        [GIFT_ATTRIBUTE]: giftOffer.mergeAttribute(
          data.attributes?.[GIFT_ATTRIBUTE],
          attribute
        )
      }
    };
  }

  #elapsed() {
    return Math.round(performance.now() - this.#startedAt);
  }

  async #readyOffer() {
    if (this.#settled) return this.#offer;
    try {
      return await withTimeout(
        this.#ready,
        READY_WAIT,
        'gift offer was not ready in time'
      );
    } catch (error) {
      if (this.#found) console.error('[gift-block] gifts skipped:', error);
      return null;
    }
  }

  #giftItems(key) {
    const variantIds = new Set(
      this.#slots.map(slot => slot.variantId).filter(id => id !== null)
    );
    return [...variantIds].map(id => ({
      id,
      quantity: 1,
      properties: { _bundle: key, _pwt: GIFT_PWT }
    }));
  }

  #begin() {
    this.#resolveReady(null);
    this.#generation += 1;
    const generation = this.#generation;
    clearTimeout(this.#capTimer);
    this.#settled = false;
    this.#built = false;
    this.#offer = null;
    this.#startedAt = performance.now();
    this.#ready = new Promise(resolve => {
      this.#resolveReady = resolve;
    });
    this.#capTimer = setTimeout(
      () => this.#settle(generation, null, 'cap'),
      READY_CAP
    );
    return generation;
  }

  #isCurrent(generation) {
    return (
      this.isConnected && generation === this.#generation && !this.#settled
    );
  }

  #settle(generation, offer, reason) {
    if (generation !== this.#generation || this.#settled) return;
    clearTimeout(this.#capTimer);
    this.#settled = true;
    this.#built = BUILT_REASONS.includes(reason);
    this.#offer = offer;
    if (!offer) {
      this.#slots = [];
      this.q(LIST)?.replaceChildren();
    }
    this.hidden = !offer;
    this.dataset.giftSettled = reason;
    const ms = this.#elapsed();
    const source = offer?.source ?? 'none';
    performance.mark(SETTLED_MARK, { detail: { reason, source, ms } });
    if (LOGGED_REASONS.includes(reason))
      console.error(
        `[gift-block] settled ${reason}`,
        `after ${ms}ms source=${source}`
      );
    this.#resolveReady(offer);
  }

  async #build() {
    const generation = this.#begin();
    this.hidden = true;
    try {
      await this.#render(generation);
    } catch (error) {
      console.error('[gift-block] gift block could not be built:', error);
      this.#settle(generation, null, 'build-error');
    }
  }

  async #render(generation) {
    if (!this.q(LIST)) return this.#settle(generation, null, 'build-error');
    if (!this.#loaded) {
      if (!giftOffer.read(this.dataset.productId)) {
        const loaded = await giftOffer.load(this.dataset.shop);
        if (!this.#isCurrent(generation)) return;
        if (loaded !== true) return this.#settle(generation, null, loaded);
      }
      this.#loaded = true;
    }

    const offer = giftOffer.find(this.dataset.productId, this.#variantId());
    this.#found = offer;
    if (!offer) return this.#settle(generation, null, 'no-offer');
    giftOffer.load(this.dataset.shop);

    const inline = offer.source === 'inline';
    const read = await Promise.all(
      offer.slots.map(entry => {
        const slot = this.#slotOf(entry);
        return inline
          ? this.#readPicked(slot, generation)
          : this.#readEvery(slot);
      })
    );
    if (!this.#isCurrent(generation)) return;

    const slots = read.filter(Boolean).slice(0, MAX_SLOTS);
    if (!slots.length) return this.#settle(generation, null, 'cards-failed');

    this.#mount(slots);
    const color = normalize(this.#selection?.color);
    if (color) this.#syncColor(slots, color);
    this.#settle(
      generation,
      offer,
      inline ? 'inline-rendered' : 'fallback-rendered'
    );
  }

  #slotOf(entry) {
    return {
      offer: entry,
      members: entry.members.map(member => ({ ...member, root: null })),
      member: null,
      variantId: null,
      index: 0,
      pending: null,
      settling: null
    };
  }

  async #fetchMembers(slot, members) {
    const cards = await giftOffer.cards(
      this.dataset.root ?? '',
      members.map(member => member.handle)
    );
    members.forEach((member, position) => {
      const html = cards[position];
      const read = html
        ? readMember(member.handle, html, slot.offer.allowedVariantIds)
        : null;
      Object.assign(member, read ?? { available: false });
    });
  }

  async #readPicked(slot, generation) {
    const picked = this.#pickMember(slot);
    if (!picked)
      return this.#isCurrent(generation) ? this.#readEvery(slot) : null;
    await this.#fetchMembers(slot, [picked]);
    if (!this.#isCurrent(generation)) return null;
    if (!this.#defaultMember(slot)) {
      const rest = slot.members.filter(
        member => member !== picked && member.available
      );
      if (rest.length) await this.#fetchMembers(slot, rest);
    }
    return this.#defaultMember(slot) ? slot : null;
  }

  async #readEvery(slot) {
    await this.#fetchMembers(slot, slot.members);
    slot.members = slot.members.filter(member => member.root);
    return this.#defaultMember(slot) ? slot : null;
  }

  #variantId() {
    return this.#selection?.variantId ?? Number(this.dataset.variantId);
  }

  #part(slot, selector) {
    return this.q(`[data-gift-slot="${slot.index}"] ${selector}`);
  }

  #mount(slots) {
    const defaults = slots.map(slot => this.#defaultMember(slot));
    const nodes = slots.map((slot, index) => {
      slot.index = index;
      const node = document.importNode(defaults[index].root, true);
      node.removeAttribute('id');
      node.dataset.giftSlot = String(index);
      return node;
    });
    this.#slots = slots;
    this.q(LIST)?.replaceChildren(...nodes);
    for (const template of this.qa(`${LIST} template`)) template.remove();

    slots.forEach((slot, index) => {
      this.#fillColors(slot);
      this.#showMember(
        slot,
        defaults[index],
        this.#initialVariant(slot, defaults[index])
      );
    });
  }

  #resolveMember(slot, fits) {
    const pool = slot.members.filter(fits);
    const color = normalize(this.#selection?.color ?? this.dataset.color);
    const initial = slot.offer.initialVariantId;
    return (
      (color && pool.find(member => normalize(member.color) === color)) ||
      pool.find(member => holdsVariant(member, initial)) ||
      pool[0] ||
      null
    );
  }

  #pickMember(slot) {
    return this.#resolveMember(slot, member => member.available);
  }

  #defaultMember(slot) {
    return this.#resolveMember(
      slot,
      member => member.root !== null && member.available
    );
  }

  #initialVariant(slot, member) {
    const initial = slot.offer.initialVariantId;
    return hasVariant(member, initial) ? initial : member.defaultVariant;
  }

  #fillColors(slot) {
    const select = this.#part(slot, COLOR);
    if (!select) return;
    select.replaceChildren(
      ...slot.members.map(member => {
        const option =
          (member.root &&
            cloneTemplate(member.root, 'template[data-gift-color-option]')
              ?.firstElementChild) ||
          new Option(member.color ?? member.title ?? '');
        option.disabled = !member.available;
        return option;
      })
    );
    this.#part(slot, COLOR_FIELD).hidden = slot.members.length < 2;
  }

  #selectColor(slot, member) {
    const select = this.#part(slot, COLOR);
    if (!select) return;
    select.selectedIndex = slot.members.indexOf(member);
    this.#syncFace(slot, COLOR_FIELD, select);
  }

  #setBusy(slot, busy) {
    const node = this.q(`[data-gift-slot="${slot.index}"]`);
    if (!node) return;
    if (busy) node.setAttribute('aria-busy', 'true');
    else node.removeAttribute('aria-busy');
  }

  #revertPending(slot) {
    if (!slot.pending) return;
    slot.pending = null;
    if (slot.member) this.#selectColor(slot, slot.member);
    this.#setBusy(slot, false);
  }

  #showMember(slot, member, variantId) {
    const previous = slot.member;
    slot.member = member;
    if (previous && previous !== member) {
      for (const selector of SWAP_REGIONS) {
        const next = member.root.querySelector(selector);
        if (next)
          this.#part(slot, selector)?.replaceWith(
            document.importNode(next, true)
          );
      }
    }

    this.#selectColor(slot, member);

    const sizeSelect = this.#part(slot, SIZE);
    if (sizeSelect) {
      sizeSelect.replaceChildren(
        ...member.sizes.map(size => {
          const option = document.createElement('option');
          option.value = String(size.id);
          option.textContent = size.label;
          option.disabled = !size.available;
          return option;
        })
      );
      this.#part(slot, SIZE_FIELD).hidden = member.sizes.length < 2;
    }

    this.#showVariant(slot, variantId);
    const selects = this.#part(slot, '[data-gift-selects]');
    if (selects)
      selects.hidden = !this.#part(slot, '[data-gift-field]:not([hidden])');
  }

  #showVariant(slot, variantId) {
    const { member } = slot;
    const id = hasVariant(member, variantId)
      ? variantId
      : member.defaultVariant;
    slot.variantId = id;

    const sizeSelect = this.#part(slot, SIZE);
    if (sizeSelect) {
      sizeSelect.value = String(id);
      this.#syncFace(slot, SIZE_FIELD, sizeSelect);
    }

    const price = cloneTemplate(
      member.root,
      `template[data-gift-price="${id}"]`
    );
    if (price)
      this.#part(slot, '[data-gift-price-slot]')?.replaceChildren(price);
  }

  #syncFace(slot, field, select) {
    const value = this.#part(slot, `${field} [data-gift-value]`);
    if (value) value.textContent = select.selectedOptions[0]?.textContent ?? '';
  }

  #sameSize(slot, member) {
    const label = slot.member.sizes.find(
      size => size.id === slot.variantId
    )?.label;
    return member.sizes.find(size => size.available && size.label === label)
      ?.id;
  }

  #variantFor(slot, member) {
    return this.#sameSize(slot, member) ?? member.defaultVariant;
  }

  #switchMember(slot, member) {
    if (member === slot.member) return this.#revertPending(slot);
    if (!member || !member.available) return;
    if (member.root) {
      this.#revertPending(slot);
      this.#showMember(slot, member, this.#variantFor(slot, member));
      return;
    }
    slot.pending = member;
    this.#selectColor(slot, member);
    this.#setBusy(slot, true);
    slot.settling = this.#settleSwitch(slot, member, this.#generation).catch(
      () => this.#revertPending(slot)
    );
  }

  async #settleSwitch(slot, member, generation) {
    await this.#fetchMembers(slot, [member]);
    if (generation !== this.#generation || !this.isConnected) return;
    if (!member.root || !member.available) {
      member.available = false;
      const option = this.#part(slot, COLOR)?.options[
        slot.members.indexOf(member)
      ];
      if (option) option.disabled = true;
    }
    if (slot.pending !== member) return;
    if (!member.available) return this.#revertPending(slot);
    this.#showMember(slot, member, this.#variantFor(slot, member));
    slot.pending = null;
    this.#setBusy(slot, false);
  }

  #onChange(event) {
    const target = event.target;
    const index = Number(target.closest('[data-gift-slot]')?.dataset.giftSlot);
    const slot = this.#slots[index];
    if (!slot) return;
    if (target.matches(COLOR))
      this.#switchMember(slot, slot.members[target.selectedIndex]);
    else if (target.matches(SIZE))
      this.#showVariant(slot, Number(target.value));
  }

  #onPointerDown(event) {
    const select = event.target.closest(SELECT);
    if (select) select.dataset.pointer = '';
  }

  #clearPointer(event) {
    event.target.closest(SELECT)?.removeAttribute('data-pointer');
  }

  #onSelection(selection) {
    if (selection?.sectionId !== this.dataset.sectionId) return;
    if (selection.productId !== Number(this.dataset.productId)) return;
    this.#select(selection);
  }

  #select(selection) {
    const previous = this.#selection;
    this.#selection = selection;
    if (!this.#loaded) {
      if (this.#settled) this.#build();
      return;
    }
    if (this.#settled && this.dataset.giftSettled === 'cap')
      return this.#build();

    const next = giftOffer.find(this.dataset.productId, selection.variantId);
    if ((next?.ruleId ?? null) !== (this.#found?.ruleId ?? null)) {
      this.#build();
      return;
    }

    const color = normalize(selection.color);
    if (!color || color === normalize(previous?.color ?? this.dataset.color))
      return;
    this.#syncColor(this.#offer ? this.#slots : [], color);
  }

  #syncColor(slots, color) {
    for (const slot of slots) {
      this.#switchMember(
        slot,
        slot.members.find(member => normalize(member.color) === color)
      );
    }
  }
}

if (!customElements.get('gift-block'))
  customElements.define('gift-block', GiftBlock);
